// @ts-nocheck
// TODO: tighten types after migration
import { API_ENDPOINTS } from '../config/api';

/**
 * Fetch active promotions from MongoDB database
 * @returns {Promise<Array>} Array of active promotions
 */
export const fetchPromotions = async (includeFuture = true) => {
  try {
    // For testing, include future promotions by default
    const url = includeFuture 
      ? `${API_ENDPOINTS.PROMOTIONS.ACTIVE}?includeFuture=true`
      : API_ENDPOINTS.PROMOTIONS.ACTIVE;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const result = await response.json();
    
    if (result.success && result.data) {
      return result.data;
    }
    
    return [];
  } catch (error) {
    return [];
  }
};

/**
 * Extract image dimensions from image name
 * Examples: "Ad-Free_200x200" -> { width: 200, height: 200 }
 *           "Ad-Free_320x50" -> { width: 320, height: 50 }
 * @param {string} name - Image name (e.g., "Ad-Free_200x200")
 * @returns {Object|null} Object with width and height, or null if not found
 */
export const getImageDimensionsFromName = (name) => {
  if (!name || typeof name !== 'string') {
    return null;
  }

  // Match pattern like "200x200" or "320x50" or "320_50" in the name (case insensitive)
  // Also handle variations like "320x50", "320_50", "320-50", etc.
  const match = name.match(/(\d+)[xX_-](\d+)/i);
  if (match) {
    return {
      width: parseInt(match[1], 10),
      height: parseInt(match[2], 10)
    };
  }

  return null;
};

/**
 * Get image by specific dimensions from promotion
 * @param {Object} promotion - Promotion object with image array
 * @param {number} targetWidth - Target width
 * @param {number} targetHeight - Target height
 * @returns {Object|null} Image object or null
 */
export const getImageByDimensions = (promotion, targetWidth, targetHeight) => {
  if (!promotion || !promotion.image || !Array.isArray(promotion.image)) {
    return null;
  }

  // Find image matching the target dimensions
  for (const img of promotion.image) {
    // Check name first
    const dimensions = getImageDimensionsFromName(img.name);
    if (dimensions && dimensions.width === targetWidth && dimensions.height === targetHeight) {
      return img;
    }
    
    // Also check URL if name doesn't have dimensions
    if (img.url) {
      const urlDimensions = getImageDimensionsFromName(img.url);
      if (urlDimensions && urlDimensions.width === targetWidth && urlDimensions.height === targetHeight) {
        return img;
      }
      
      // Check if URL contains the dimension pattern (e.g., "320x50" in URL)
      const dimensionPattern = `${targetWidth}x${targetHeight}`;
      if (img.url.includes(dimensionPattern) || img.url.includes(`${targetWidth}_${targetHeight}`) || img.url.includes(`${targetWidth}-${targetHeight}`)) {
        return img;
      }
    }
  }

  return null;
};

/**
 * Get appropriate promotion image based on page type and size requirements
 * @param {Object} promotion - Promotion object
 * @param {string} pageType - Page type (e.g., 'blog-header', 'sidebar', etc.)
 * @param {number} targetWidth - Target width in pixels
 * @param {number} targetHeight - Target height in pixels
 * @returns {Object|null} Image object with url and name, or null
 */
export const getPromotionImage = (promotion, pageType = 'default', targetWidth = null, targetHeight = null) => {
  if (!promotion || !promotion.image || !Array.isArray(promotion.image) || promotion.image.length === 0) {
    return null;
  }

  // If specific dimensions are provided, use them
  if (targetWidth && targetHeight) {
    const specificImage = getImageByDimensions(promotion, targetWidth, targetHeight);
    if (specificImage) {
      return specificImage;
    }
  }

  // Page-specific image selection
  switch (pageType) {
    case 'blog-header':
      // Blog header ALWAYS needs 320x50 image - don't fallback to other sizes
      const blogHeaderImage = getImageByDimensions(promotion, 320, 50);
      if (blogHeaderImage) {
        return blogHeaderImage;
      }
      // If 320x50 not found by dimensions, try to find any image with "320x50", "320_50", "320-50" in the name or URL (case insensitive)
      for (const img of promotion.image) {
        const searchText = `${img.name || ''} ${img.url || ''}`.toLowerCase();
        if (searchText.includes('320x50') || searchText.includes('320_50') || searchText.includes('320-50')) {
          return img;
        }
      }
      // Last resort: return null if no 320x50 image found
      return null;
    
    case 'sidebar':
    case 'desktop':
      // Sidebar/desktop ALWAYS needs 200x200 image - don't fallback to other sizes
      const sidebarImage = getImageByDimensions(promotion, 200, 200);
      if (sidebarImage) {
        return sidebarImage;
      }
      // If 200x200 not found by dimensions, try to find any image with "200x200" in the name or URL (case insensitive)
      for (const img of promotion.image) {
        const searchText = `${img.name || ''} ${img.url || ''}`.toLowerCase();
        if (searchText.includes('200x200') || searchText.includes('200_200') || searchText.includes('200-200')) {
          return img;
        }
      }
      // Last resort: return null if no 200x200 image found
      return null;
    
    case 'mobile':
      // Mobile needs 320x50 image
      const mobileImage = getImageByDimensions(promotion, 320, 50);
      if (mobileImage) {
        return mobileImage;
      }
      // Try to find any image with "320x50" in the name
      for (const img of promotion.image) {
        if (img.name && img.name.includes('320x50')) {
          return img;
        }
      }
      return null;
    
    default:
      // Default: try to find 200x200, then 320x50, then first image
      return getImageByDimensions(promotion, 200, 200) 
        || getImageByDimensions(promotion, 320, 50) 
        || promotion.image[0];
  }
};

/**
 * Check if promotion is currently active based on dates
 * @param {Object} promotion - Promotion object with startDate and endDate
 * @returns {boolean} True if promotion is active
 */
export const isPromotionActive = (promotion) => {
  if (!promotion || !promotion.startDate || !promotion.endDate) {
    return false;
  }
  
  const currentDate = new Date();
  const startDate = new Date(promotion.startDate);
  const endDate = new Date(promotion.endDate);
  
  return currentDate >= startDate && currentDate <= endDate;
};

